import { backgroundGradients, borderRadius, palette, spaces } from '@kimuno/theme';
import { Box, DialogContent, Paper } from '@mui/material';
import { debounce } from 'debounce';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { updateGeneratedDocument as updateGeneratedDocumentAction } from '../../../../actions/aiAssistant.actions';
import { getCurrentConversationId } from '../../../../reducers/aiAssistant.selectors';
import services from '../../../../services';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../state/AIAssistantContextProvider';
import {
	AIAssistantEditorMode,
	aiAssistantEditorContext,
} from '../../../../state/AIAssistantEditorProvider';
import { getAIAssistantPageSource } from '../../../../utils/getAIAssistantPageSource';
import useAction from '../../../../utils/useAction';
import useRouter from '../../../../utils/useRouter';
import useScreenSize from '../../../../utils/useScreenSize';
import SwipeableDrawer from '../../../SwipeableDrawer/SwipeableDrawer';
import { htmlToMarkdown } from '../../../utils/contentConverter';
import PodcastDetails from '../../Page/PodcastDetails';
import { TEXT_EDITOR_WIDTH } from '../constants';
import AIAssistantEditorTopBar from './AIAssistantEditorTopBar';
import AIAssistantTextEditorContent from './AIAssistantTextEditorContent';
import AIAssistantTextEditorFloatingToolbar from './AIAssistantTextEditorFloatingToolbar';
import AIAssistantTextEditorMobileHeader from './AIAssistantTextEditorMobileHeader';

const richTextSaveTimeoutMs = 2000;

const getPaperStyles = (isPageMode: boolean) => {
	const commonStyles = {
		borderBottomRightRadius: '0 !important',
		borderTopRightRadius: '0 !important',
		background: backgroundGradients.aiAssistant,
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		borderRightColor: palette.primary.contrast,
		borderRightWidth: '1px',
		borderRightStyle: 'solid',
	};

	return isPageMode
		? {
				...commonStyles,
				width: '100%',
			}
		: {
				...commonStyles,
				width: `${TEXT_EDITOR_WIDTH}px`,
				gridColumn: '1 / 2',
			};
};
const getDialogContentStyles = (isPageMode: boolean, isPodcast: boolean) => ({
	flex: 1,
	padding: 0,
	backgroundColor: isPodcast
		? palette.background.paperElevation0
		: isPageMode
			? palette.background.paperElevation2
			: palette.background.paperElevation1,
	overflow: 'auto',
	display: 'flex',
	flexDirection: 'column',
	...(isPageMode && {
		gap: spaces.lg,
		margin: spaces.md,
		borderRadius: `${borderRadius.sm} ${borderRadius.sm} 0 0`,
		border: `1px solid ${palette.elevation.outlined}`,
	}),
});

const AIAssistantTextEditorContainer = () => {
	const {
		isTextEditorOpen,
		openTextEditor,
		closeTextEditor,
		editorData,
		setTextEditorContent,
		messageId,
		documentId,
		mode,
		markContentChanged,
	} = useContext(aiAssistantEditorContext);

	const currentContent = useRef(editorData.content);
	const conversationId = useSelector(getCurrentConversationId);
	const isTextEditedRef = useRef(false);
	const updateGeneratedDocument = useAction(updateGeneratedDocumentAction);
	const {
		match: {
			location: { pathname },
		},
	} = useRouter();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isXsOrSmScreen = isXsScreen || isSmScreen;
	const { displayMode } = useContext(aiAssistantContext);
	const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
	const isPodcast = mode === AIAssistantEditorMode.PODCAST;

	// The save body is re-read from a ref on every call, so the debounced wrapper
	// below can stay the same function for the lifetime of the editor. Several of
	// the values it closes over change identity on every render (the provider's
	// setTextEditorContent, useAction's updateGeneratedDocument), so a deps list
	// would rebuild the wrapper mid-countdown and the cleanup effect would clear
	// the pending save — a typed edit was dropped whenever anything re-rendered
	// inside the 2 s window (FEAT-408).
	const saveContent = (
		newContent: string,
		editedDocumentId: string | null,
		editedMessageId: string | null
	) => {
		// The save fires 2 s after the last keystroke, and the editor can be moved
		// elsewhere in the meantime without remounting: documentId is Redux state,
		// and openTextEditor({ messageId }) swaps the playground message through
		// provider state. The edit belongs to what it was typed into — it is
		// persisted to that document, while the editor state, which may now hold
		// something else, is left untouched. A playground message carries no
		// documentId, so both have to match or two messages compare equal on null.
		const isEditedTargetStillOpen = editedDocumentId === documentId && editedMessageId === messageId;

		if (isEditedTargetStillOpen) {
			if (newContent === currentContent.current) {
				return;
			}
			setTextEditorContent(newContent);
			currentContent.current = newContent;
			if (isXsOrSmScreen) {
				markContentChanged();
			}

			if (mode !== AIAssistantEditorMode.STREAM && !isTextEditedRef.current) {
				isTextEditedRef.current = true;
				const pageSource = getAIAssistantPageSource(pathname);
				services.track.ampliTrackTextEditedEvent(conversationId ?? '', pageSource);
			}
		}

		if (editedDocumentId) {
			updateGeneratedDocument({
				documentId: editedDocumentId,
				payload: {
					content: htmlToMarkdown(newContent),
				},
			});
		}
	};

	const saveContentRef = useRef(saveContent);
	useEffect(() => {
		saveContentRef.current = saveContent;
	});

	const debouncedSave = useMemo(
		() =>
			debounce(
				(newContent: string, editedDocumentId: string | null, editedMessageId: string | null) =>
					saveContentRef.current(newContent, editedDocumentId, editedMessageId),
				richTextSaveTimeoutMs
			),
		[]
	);

	const pendingSaveDocumentIdRef = useRef<string | null>(null);

	// Deliberately not memoised: it only forwards to the stable debounce above, so
	// a fresh identity cannot restart a countdown, and the editor reads this prop
	// through a ref of its own. Capturing documentId here binds the pending edit
	// to the document open at the keystroke rather than at the save.
	//
	// The debounce holds one pending call, and each new one replaces its
	// arguments. Edits to the same document are meant to coalesce that way, but an
	// edit to a different document would drop the first document's edit entirely,
	// so that one is settled before the new document takes the slot.
	const handleContentChange = (newContent: string) => {
		if (pendingSaveDocumentIdRef.current !== documentId) {
			debouncedSave.flush?.();
		}
		pendingSaveDocumentIdRef.current = documentId;
		debouncedSave(newContent, documentId, messageId);
	};

	useEffect(() => {
		isTextEditedRef.current = false;
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (conversationId && mode !== AIAssistantEditorMode.STREAM) {
			isTextEditedRef.current = false;
		}
	}, [conversationId, messageId, documentId, mode]);

	useEffect(() => {
		currentContent.current = editorData.content;
	}, [editorData.content]);

	useEffect(() => {
		return () => {
			debouncedSave.clear?.();
		};
	}, [debouncedSave]);

	if (isXsOrSmScreen) {
		return (
			<SwipeableDrawer
				open={isTextEditorOpen}
				onOpen={openTextEditor}
				onClose={closeTextEditor}
				pullSnap={true}
				initialHeight="full"
				draggable={!isPodcast}
				backgroundColor={isPodcast ? palette.background.paperElevation0 : undefined}
				data-testid="ai-assistant-swipeable-edge"
			>
				{/* The drawer keeps its children mounted while closed; the editor must
            not exist invisibly or a later mode switch unmounts it mid-init. */}
				{isTextEditorOpen && (
					<Box
						sx={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: spaces.xs,
							position: 'relative',
							marginX: `-${spaces.md}`,
						}}
					>
						<AIAssistantTextEditorMobileHeader />
						<Box
							// Remount on panel swap; in-place reconcile crashes on tippy DOM cleanup.
							key={isPodcast ? 'podcast' : 'editor'}
							data-testid="ai-editor-panel-slot"
							sx={{
								flex: 1,
								overflow: 'auto',
							}}
						>
							{isPodcast ? (
								<PodcastDetails />
							) : (
								<AIAssistantTextEditorContent
									content={editorData.content}
									onContentChange={handleContentChange}
								/>
							)}
						</Box>
						{!isPodcast && <AIAssistantTextEditorFloatingToolbar />}
					</Box>
				)}
			</SwipeableDrawer>
		);
	}

	return (
		<Paper data-testid="ai-assistant-text-editor-container" sx={getPaperStyles(isPageMode)}>
			<AIAssistantEditorTopBar />
			<DialogContent
				// Remount on panel swap; in-place reconcile crashes on tippy DOM cleanup.
				key={isPodcast ? 'podcast' : 'editor'}
				data-testid="ai-editor-panel-slot"
				sx={getDialogContentStyles(isPageMode, isPodcast)}
			>
				{isPodcast ? (
					<PodcastDetails />
				) : (
					<AIAssistantTextEditorContent
						content={editorData.content}
						onContentChange={handleContentChange}
					/>
				)}
			</DialogContent>
		</Paper>
	);
};

export default AIAssistantTextEditorContainer;
